import { Tab } from "@mui/base/Tab";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import clsx from "clsx";
import { useMemo, useState } from "react";

import { WizardStep, useWizard } from "~/contexts/WizardContext";
import { ALLI_VERIFICATION_NAME } from "~/helpers/consts";

import { Domain } from "../../../api/Api";
import Spinner from "../../ui/spinner/Spinner";
import OriginsStatusModal from "../OriginsStatusModal";
import { wizardStepsLabels } from "../helpers";
import IsNoSetup from "../isNoSetup/IsNoSetup";
import Certificate from "./Certificate";
import { Configure } from "./Configure.tsx";
import { Confirmation } from "./Confirmation.tsx";
import DNS from "./DNS";
import Install from "./Install";
import Nameservers from "./Nameservers";
import Routing from "./Routing";
import Verification from "./Verification";
import styles from "./Wizzard.module.scss";

interface WizzardProps {
  isLoading?: boolean;
  domain: Domain;
}

type WizardRenderableStep = Exclude<WizardStep, "setup">;

type StepView = { id: WizardRenderableStep; title: string };

const Wizzard = ({ isLoading, domain }: WizzardProps) => {
  const {
    navigateToStep,
    currentStep,
    getStepError,
    getStepTitle,
    canAccessStep,
  } = useWizard();
  const [isOriginsModalOpen, setIsOriginsModalOpen] = useState(false);

  const steps = domain.wizard_steps ?? [];

  const verificationData =
    domain?.system_config?.filter(
      (record) =>
        typeof record?.[0] === "string" &&
        record[0].startsWith(ALLI_VERIFICATION_NAME),
    ) ?? [];

  const filteredSteps = useMemo<StepView[]>(() => {
    return steps
      .filter((s): s is WizardRenderableStep => s !== "setup")
      .map((stepId) => {
        const label = wizardStepsLabels.find((l) => l.id === stepId);

        return {
          id: stepId,
          title: label?.title ?? stepId,
        };
      });
  }, [steps]);

  const tabSteps = filteredSteps.map((step) => step.id);

  const handleTabClick = (step: WizardStep) => {
    if (step === "dns") {
      setIsOriginsModalOpen(true);
    }

    navigateToStep(step);
  };

  const renderStepContent = (step: WizardStep) => {
    switch (step) {
      case "verification":
        return <Verification data={verificationData} />;
      case "certificate":
        return <Certificate isWizzard />;
      case "dns": {
        return <DNS />;
      }
      case "routing":
        return <Routing isWizzard domain={domain} />;
      case "ns":
        return <Nameservers isWizzard domain={domain} />;
      case "confirmation":
        return <Confirmation isWizzard domain={domain} />;
      case "install":
        return <Install isWizzard data={verificationData} />;
      case "configure":
        return <Configure isWizzard domain={domain} />;
      default:
        return null;
    }
  };

  if (isLoading || (domain.integration_type && filteredSteps.length === 0)) {
    return (
      <div className={styles.card}>
        <Spinner />
      </div>
    );
  }

  if (currentStep === "setup") {
    return <IsNoSetup />;
  }

  const activeTabValue =
    currentStep && tabSteps.includes(currentStep) ? currentStep : tabSteps[0];

  return (
    <div>
      <div className={styles.card}>
        <Tabs value={activeTabValue} className={styles.tabs}>
          <TabsList className={styles.tabsList}>
            <div className={styles.stepsDivider} />
            {filteredSteps?.map((step, index) => {
              const hasError = getStepError(step.id);
              const title = getStepTitle(step.id, step.title);
              const isDisabled = !canAccessStep(step.id);

              return (
                <Tab
                  key={step.id}
                  disabled={isDisabled}
                  value={step.id}
                  onClick={() => handleTabClick(step.id)}
                  className={clsx(
                    styles.tab,
                    // @ts-expect-error TS2339 non-existing className
                    isDisabled && styles.disabled,
                  )}
                >
                  <span
                    className={clsx(
                      styles.stepNumber,
                      hasError && styles.error,
                    )}
                  >
                    {index + 1}
                  </span>
                  <span
                    className={clsx(styles.stepTitle, hasError && styles.error)}
                  >
                    {title}
                  </span>
                </Tab>
              );
            })}
          </TabsList>

          {filteredSteps.map((step) => (
            <TabPanel key={step.id} value={step.id}>
              {renderStepContent(step.id)}
            </TabPanel>
          ))}
        </Tabs>
      </div>

      <OriginsStatusModal
        isOpen={isOriginsModalOpen}
        closeModal={() => setIsOriginsModalOpen(false)}
        domainId={domain.id}
      />
    </div>
  );
};

export default Wizzard;
